import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart2, TrendingUp, Users, Eye, Clock,
  Monitor, Smartphone, Tablet, Globe, RefreshCw,
  ArrowUpRight, ArrowDownRight, Activity, MousePointer,
  ExternalLink, Loader2, MapPin, Crosshair, Zap, Calendar,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Country flag emoji helper
const getCountryFlag = (countryCode) => {
  if (!countryCode || countryCode === 'XX') return '🌍';
  const codePoints = countryCode
    .toUpperCase()
    .split('')
    .map(char => 127397 + char.charCodeAt());
  return String.fromCodePoint(...codePoints);
};

// Format large numbers
const formatNumber = (num) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num?.toString() || '0';
};

// Get device icon
const getDeviceIcon = (type) => {
  switch (type) {
    case 'mobile': return Smartphone;
    case 'tablet': return Tablet;
    default: return Monitor;
  }
};

// Simple Bar Chart Component
const SimpleBarChart = ({ data, maxHeight = 100 }) => {
  if (!data || data.length === 0) return <div className="text-gray-400 text-sm">No data</div>;
  
  const maxValue = Math.max(...data.map(d => d.views || d.count || 0));
  
  return (
    <div className="flex items-end gap-1 h-24">
      {data.slice(-14).map((item, index) => {
        const value = item.views || item.count || 0;
        const height = maxValue > 0 ? (value / maxValue) * maxHeight : 0;
        return (
          <div
            key={index}
            className="flex-1 bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer group relative"
            style={{ height: `${height}%`, minHeight: value > 0 ? '4px' : '0' }}
            title={`${item.date || item.hour}: ${value} views`}
          >
            <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
              {item.date || `${item.hour}:00`}: {value}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Traffic Time Heatmap Component
const TrafficHeatmap = ({ matrixData }) => {
  if (!matrixData || matrixData.length === 0) {
    return <div className="text-gray-400 text-sm text-center py-4">No traffic pattern data yet</div>;
  }

  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const hours = Array.from({length: 24}, (_, i) => i);
  
  // Build matrix
  const matrix = {};
  matrixData.forEach(item => {
    const key = `${item.day}-${item.hour}`;
    matrix[key] = item.views;
  });
  
  const maxViews = Math.max(...Object.values(matrix), 1);

  const getHeatColor = (value) => {
    if (!value) return 'bg-gray-100';
    const intensity = value / maxViews;
    if (intensity > 0.8) return 'bg-blue-600';
    if (intensity > 0.6) return 'bg-blue-500';
    if (intensity > 0.4) return 'bg-blue-400';
    if (intensity > 0.2) return 'bg-blue-300';
    return 'bg-blue-200';
  };

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[600px]">
        <div className="flex gap-1 mb-1 ml-12">
          {hours.filter((_, i) => i % 4 === 0).map(h => (
            <div key={h} className="text-xs text-gray-400 w-16 text-center">{h}:00</div>
          ))}
        </div>
        {days.map(day => (
          <div key={day} className="flex items-center gap-1 mb-1">
            <div className="w-10 text-xs text-gray-500 text-right pr-2">{day}</div>
            {hours.map(hour => {
              const value = matrix[`${day}-${hour}`] || 0;
              return (
                <div
                  key={hour}
                  className={`w-4 h-4 rounded-sm ${getHeatColor(value)} cursor-pointer transition-transform hover:scale-125`}
                  title={`${day} ${hour}:00 - ${value} views`}
                />
              );
            })}
          </div>
        ))}
        <div className="flex items-center gap-2 mt-3 ml-12">
          <span className="text-xs text-gray-400">Less</span>
          <div className="flex gap-1">
            {['bg-gray-100', 'bg-blue-200', 'bg-blue-300', 'bg-blue-400', 'bg-blue-500', 'bg-blue-600'].map((color, i) => (
              <div key={i} className={`w-3 h-3 rounded-sm ${color}`} />
            ))}
          </div>
          <span className="text-xs text-gray-400">More</span>
        </div>
      </div>
    </div>
  );
};

// Click Heatmap Visual Component
const ClickHeatmapVisual = ({ clickData }) => {
  if (!clickData || !clickData.topElements || clickData.topElements.length === 0) {
    return (
      <div className="text-center py-8">
        <Crosshair className="w-12 h-12 text-gray-300 mx-auto mb-2" />
        <p className="text-gray-400 text-sm">Click tracking active</p>
        <p className="text-xs text-gray-300 mt-1">Data will appear as visitors interact</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm text-gray-600">
          <span className="font-semibold text-gray-900">{formatNumber(clickData.totalClicks)}</span> total clicks tracked
        </div>
      </div>
      
      {/* Click by element type */}
      {clickData.byElementType && Object.keys(clickData.byElementType).length > 0 && (
        <div className="space-y-2">
          <p className="text-xs text-gray-500 uppercase tracking-wide">By Element Type</p>
          {Object.entries(clickData.byElementType).slice(0, 5).map(([type, count]) => {
            const total = Object.values(clickData.byElementType).reduce((a, b) => a + b, 0);
            const percent = Math.round((count / total) * 100);
            return (
              <div key={type} className="flex items-center gap-2">
                <div className="w-20 text-sm text-gray-700 capitalize">{type}</div>
                <div className="flex-1 bg-gray-100 rounded-full h-2">
                  <div 
                    className="bg-purple-500 h-2 rounded-full transition-all"
                    style={{ width: `${percent}%` }}
                  />
                </div>
                <div className="w-12 text-sm text-gray-500 text-right">{percent}%</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Top clicked elements */}
      <div className="space-y-2 pt-3 border-t border-gray-100">
        <p className="text-xs text-gray-500 uppercase tracking-wide">Most Clicked Elements</p>
        {clickData.topElements.slice(0, 5).map((el, index) => (
          <div key={index} className="flex items-center justify-between text-sm">
            <span className="text-gray-700 truncate flex-1" title={el.text}>
              &quot;{el.text?.substring(0, 30)}{el.text?.length > 30 ? '...' : ''}&quot;
            </span>
            <span className="text-gray-500 ml-2">{el.count} clicks</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Hourly Bar Chart for Daily View
const HourlyBarChart = ({ data }) => {
  if (!data || data.length === 0) return <div className="text-gray-400 text-sm">No data</div>;
  
  const maxValue = Math.max(...data.map(d => d.views || 0), 1);
  
  return (
    <div className="space-y-1">
      <div className="flex items-end gap-0.5 h-32">
        {data.map((item, index) => {
          const height = maxValue > 0 ? (item.views / maxValue) * 100 : 0;
          return (
            <div key={index} className="flex-1 flex flex-col items-center group relative">
              <div
                className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors cursor-pointer"
                style={{ height: `${height}%`, minHeight: item.views > 0 ? '2px' : '0' }}
              >
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 hidden group-hover:block bg-gray-900 text-white text-xs px-2 py-1 rounded whitespace-nowrap z-10">
                  {item.hour}:00 - {item.views} views, {item.visitors} visitors
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex justify-between text-xs text-gray-400 px-1">
        <span>12am</span>
        <span>6am</span>
        <span>12pm</span>
        <span>6pm</span>
        <span>11pm</span>
      </div>
    </div>
  );
};

// Daily Analytics View Component
const DailyAnalyticsView = ({ token }) => {
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [dailyData, setDailyData] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [loading, setLoading] = useState(true);

  // Fetch available dates on mount
  useEffect(() => {
    const fetchDates = async () => {
      try {
        const response = await fetch(`${API_URL}/api/analytics/dates?limit=60`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          setAvailableDates(result.dates || []);
        }
      } catch (err) {
        console.error('Error fetching dates:', err);
      }
    };
    fetchDates();
  }, [token]);

  // Fetch daily analytics when date changes
  useEffect(() => {
    const fetchDaily = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_URL}/api/analytics/daily/${selectedDate}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const result = await response.json();
          setDailyData(result.data);
        }
      } catch (err) {
        console.error('Error fetching daily analytics:', err);
      } finally {
        setLoading(false);
      }
    };
    if (selectedDate) fetchDaily();
  }, [selectedDate, token]);

  const navigateDay = (direction) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + direction);
    const newDate = current.toISOString().split('T')[0];
    // Don't go beyond today
    if (new Date(newDate) <= new Date()) {
      setSelectedDate(newDate);
    }
  };

  const formatDateDisplay = (dateStr) => {
    const date = new Date(dateStr);
    const today = new Date().toISOString().split('T')[0];
    const yesterday = new Date(Date.now() - 86400000).toISOString().split('T')[0];
    
    if (dateStr === today) return 'Today';
    if (dateStr === yesterday) return 'Yesterday';
    return date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });
  };

  const getChangeIndicator = (current, previous) => {
    if (!previous || previous === 0) return null;
    const change = Math.round(((current - previous) / previous) * 100);
    if (change === 0) return null;
    return (
      <span className={`flex items-center text-xs ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
        {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
        {Math.abs(change)}% vs prev day
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const summary = dailyData?.summary || {};

  return (
    <div className="space-y-6">
      {/* Date Selector */}
      <div className="bg-white border border-gray-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigateDay(-1)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Previous day"
          >
            <ChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-blue-600" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={new Date().toISOString().split('T')[0]}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-lg font-semibold text-gray-900">
              {formatDateDisplay(selectedDate)}
            </span>
          </div>
          
          <button
            onClick={() => navigateDay(1)}
            disabled={selectedDate === new Date().toISOString().split('T')[0]}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-30"
            title="Next day"
          >
            <ChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        
        {/* Quick date buttons */}
        {availableDates.length > 0 && (
          <div className="flex gap-2 mt-3 flex-wrap">
            {availableDates.slice(0, 7).map(date => (
              <button
                key={date}
                onClick={() => setSelectedDate(date)}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  selectedDate === date 
                    ? 'bg-blue-600 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {formatDateDisplay(date)}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Daily Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
              <Eye className="w-5 h-5 text-blue-600" />
            </div>
          </div>
          <div className="mt-3">
            <p className="text-2xl font-bold text-gray-900">{formatNumber(summary.pageViews)}</p>
            <p className="text-sm text-gray-500">Page Views</p>
          </div>
          {getChangeIndicator(summary.pageViews, summary.previousDay?.pageViews)}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
            <Users className="w-5 h-5 text-purple-600" />
          </div>
          <div className="mt-3">
            <p className="text-2xl font-bold text-gray-900">{formatNumber(summary.uniqueVisitors)}</p>
            <p className="text-sm text-gray-500">Unique Visitors</p>
          </div>
          {getChangeIndicator(summary.uniqueVisitors, summary.previousDay?.uniqueVisitors)}
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
            <Activity className="w-5 h-5 text-green-600" />
          </div>
          <div className="mt-3">
            <p className="text-2xl font-bold text-gray-900">{formatNumber(summary.sessions)}</p>
            <p className="text-sm text-gray-500">Sessions</p>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
            <Clock className="w-5 h-5 text-amber-600" />
          </div>
          <div className="mt-3">
            <p className="text-2xl font-bold text-gray-900">{summary.peakHour}:00</p>
            <p className="text-sm text-gray-500">Peak Hour</p>
          </div>
        </div>
      </div>

      {/* Hourly Breakdown */}
      <div className="bg-white border border-gray-200 rounded-xl p-5">
        <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
          <Clock className="w-4 h-4 text-blue-600" />
          Hourly Traffic Breakdown
        </h3>
        <HourlyBarChart data={dailyData?.hourly} />
      </div>

      {/* Details Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Top Pages */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Globe className="w-4 h-4 text-purple-600" />
            Top Pages
          </h3>
          {dailyData?.topPages?.length > 0 ? (
            <div className="space-y-3">
              {dailyData.topPages.map((page, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-gray-400 text-sm w-5">{index + 1}.</span>
                    <span className="text-sm text-gray-700 truncate" title={page.page}>
                      {page.page === '/' ? 'Homepage' : page.page}
                    </span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{page.views}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No page data for this day</p>
          )}
        </div>

        {/* Devices & Browsers */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <Monitor className="w-4 h-4 text-blue-600" />
            Devices & Browsers
          </h3>
          {dailyData?.devices && Object.keys(dailyData.devices).length > 0 ? (
            <div className="space-y-4">
              <div className="space-y-2">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Devices</p>
                {Object.entries(dailyData.devices).map(([device, count]) => {
                  const Icon = getDeviceIcon(device);
                  const total = Object.values(dailyData.devices).reduce((a, b) => a + b, 0);
                  const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                  return (
                    <div key={device} className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-gray-400" />
                      <span className="text-sm text-gray-700 capitalize flex-1">{device}</span>
                      <span className="text-sm text-gray-500">{percent}%</span>
                    </div>
                  );
                })}
              </div>
              
              {dailyData?.browsers && Object.keys(dailyData.browsers).length > 0 && (
                <div className="space-y-2 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Browsers</p>
                  {Object.entries(dailyData.browsers).slice(0, 4).map(([browser, count]) => {
                    const total = Object.values(dailyData.browsers).reduce((a, b) => a + b, 0);
                    const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                    return (
                      <div key={browser} className="flex items-center gap-2">
                        <span className="text-sm text-gray-700 capitalize flex-1">{browser}</span>
                        <span className="text-sm text-gray-500">{percent}%</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No device data for this day</p>
          )}
        </div>

        {/* Countries & Referrers */}
        <div className="bg-white border border-gray-200 rounded-xl p-5">
          <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="w-4 h-4 text-green-600" />
            Visitors by Location
          </h3>
          {dailyData?.countries?.length > 0 ? (
            <div className="space-y-3 mb-4">
              {dailyData.countries.slice(0, 5).map((country, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-lg">{getCountryFlag(country.countryCode)}</span>
                    <span className="text-sm text-gray-700">{country.country}</span>
                  </div>
                  <span className="text-sm font-medium text-gray-900">{country.visitors}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm mb-4">No location data for this day</p>
          )}
          
          {dailyData?.referrers?.length > 0 && (
            <>
              <h4 className="font-medium text-gray-700 text-sm mb-2 pt-3 border-t border-gray-100">Top Referrers</h4>
              <div className="space-y-2">
                {dailyData.referrers.map((ref, index) => (
                  <div key={index} className="flex items-center justify-between text-sm">
                    <span className="text-gray-600 truncate flex-1">
                      {ref.referrer?.replace(/^https?:\/\//, '').split('/')[0]}
                    </span>
                    <span className="text-gray-900 font-medium ml-2">{ref.count}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const AnalyticsDashboard = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(30);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchAnalytics = useCallback(async (showRefreshing = false) => {
    if (showRefreshing) setRefreshing(true);
    else setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/analytics/all?days=${period}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const result = await response.json();
        setData(result.data);
        setError(null);
      } else {
        setError('Failed to load analytics');
      }
    } catch (err) {
      setError('Failed to connect to analytics service');
      console.error('Analytics error:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token, period]);

  useEffect(() => {
    fetchAnalytics();
    const interval = setInterval(() => fetchAnalytics(true), 60000);
    return () => clearInterval(interval);
  }, [fetchAnalytics]);

  const getChangePercent = (current, previous) => {
    if (!previous || previous === 0) return current > 0 ? 100 : 0;
    return Math.round(((current - previous) / previous) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 text-center">
        <Activity className="w-12 h-12 text-amber-500 mx-auto mb-3" />
        <h3 className="font-medium text-amber-800">{error}</h3>
        <p className="text-sm text-amber-600 mt-1">Analytics tracking is ready. Data will appear once visitors start browsing.</p>
        <button onClick={() => fetchAnalytics()} className="mt-4 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700">
          Try Again
        </button>
      </div>
    );
  }

  const overview = data?.overview || {};
  const viewsChange = getChangePercent(overview.todayViews, overview.yesterdayViews);

  const tabs = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    { id: 'daily', label: 'Daily View', icon: Calendar },
    { id: 'geographic', label: 'Geographic', icon: MapPin },
    { id: 'clicks', label: 'Click Tracking', icon: Crosshair },
    { id: 'traffic', label: 'Traffic Patterns', icon: Zap }
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <BarChart2 className="w-5 h-5 text-blue-600" />
            Analytics Dashboard
          </h2>
          <p className="text-sm text-gray-600 mt-1">Track your website performance and visitor insights</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value))}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button
            onClick={() => fetchAnalytics(true)}
            disabled={refreshing}
            className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            title="Refresh data"
          >
            <RefreshCw className={`w-5 h-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Live Visitors Badge */}
      {data?.liveVisitors > 0 && (
        <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500"></span>
          </span>
          <span className="text-green-800 font-medium">{data.liveVisitors} visitor{data.liveVisitors !== 1 ? 's' : ''} online now</span>
        </div>
      )}

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg overflow-x-auto">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-white text-blue-600 shadow-sm' 
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
          {/* Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Eye className="w-5 h-5 text-blue-600" />
                </div>
                {viewsChange !== 0 && (
                  <span className={`flex items-center text-sm font-medium ${viewsChange >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                    {viewsChange >= 0 ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
                    {Math.abs(viewsChange)}%
                  </span>
                )}
              </div>
              <div className="mt-3">
                <p className="text-2xl font-bold text-gray-900">{formatNumber(overview.totalPageViews)}</p>
                <p className="text-sm text-gray-500">Page Views</p>
              </div>
              <p className="text-xs text-gray-400 mt-2">{overview.todayViews || 0} today</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <Users className="w-5 h-5 text-purple-600" />
              </div>
              <div className="mt-3">
                <p className="text-2xl font-bold text-gray-900">{formatNumber(overview.uniqueVisitors)}</p>
                <p className="text-sm text-gray-500">Unique Visitors</p>
              </div>
              <p className="text-xs text-gray-400 mt-2">{overview.todayVisitors || 0} today</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div className="mt-3">
                <p className="text-2xl font-bold text-gray-900">{overview.avgSessionDurationFormatted || '0m 0s'}</p>
                <p className="text-sm text-gray-500">Avg. Session</p>
              </div>
              <p className="text-xs text-gray-400 mt-2">{overview.totalSessions || 0} total sessions</p>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <MousePointer className="w-5 h-5 text-amber-600" />
              </div>
              <div className="mt-3">
                <p className="text-2xl font-bold text-gray-900">{overview.bounceRate || 0}%</p>
                <p className="text-sm text-gray-500">Bounce Rate</p>
              </div>
              <p className="text-xs text-gray-400 mt-2">Single-page visits</p>
            </div>
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-600" />
                Daily Traffic
              </h3>
              <SimpleBarChart data={data?.dailyTraffic} />
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>Older</span>
                <span>Recent</span>
              </div>
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-4 h-4 text-green-600" />
                Traffic by Hour (Last 7 Days)
              </h3>
              <SimpleBarChart data={data?.hourlyTraffic} />
              <div className="flex justify-between text-xs text-gray-400 mt-2">
                <span>12am</span>
                <span>12pm</span>
                <span>11pm</span>
              </div>
            </div>
          </div>

          {/* Bottom Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Globe className="w-4 h-4 text-purple-600" />
                Top Pages
              </h3>
              {data?.topPages?.length > 0 ? (
                <div className="space-y-3">
                  {data.topPages.slice(0, 5).map((page, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-gray-400 text-sm w-5">{index + 1}.</span>
                        <span className="text-sm text-gray-700 truncate" title={page.page}>
                          {page.page === '/' ? 'Homepage' : page.page}
                        </span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-gray-900 font-medium">{page.views}</span>
                        <span className="text-gray-400">views</span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No page data yet</p>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <Monitor className="w-4 h-4 text-blue-600" />
                Devices & Browsers
              </h3>
              {data?.devices && Object.keys(data.devices).length > 0 ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Devices</p>
                    {Object.entries(data.devices).map(([device, count]) => {
                      const Icon = getDeviceIcon(device);
                      const total = Object.values(data.devices).reduce((a, b) => a + b, 0);
                      const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                      return (
                        <div key={device} className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-700 capitalize flex-1">{device}</span>
                          <span className="text-sm text-gray-500">{percent}%</span>
                        </div>
                      );
                    })}
                  </div>
                  
                  {data?.browsers && Object.keys(data.browsers).length > 0 && (
                    <div className="space-y-2 pt-3 border-t border-gray-100">
                      <p className="text-xs text-gray-500 uppercase tracking-wide">Browsers</p>
                      {Object.entries(data.browsers).slice(0, 4).map(([browser, count]) => {
                        const total = Object.values(data.browsers).reduce((a, b) => a + b, 0);
                        const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                        return (
                          <div key={browser} className="flex items-center gap-2">
                            <span className="text-sm text-gray-700 capitalize flex-1">{browser}</span>
                            <span className="text-sm text-gray-500">{percent}%</span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-400 text-sm">No device data yet</p>
              )}
            </div>

            <div className="bg-white border border-gray-200 rounded-xl p-5">
              <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
                <ExternalLink className="w-4 h-4 text-green-600" />
                Top Referrers
              </h3>
              {data?.topReferrers?.length > 0 ? (
                <div className="space-y-3">
                  {data.topReferrers.slice(0, 5).map((ref, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700 truncate flex-1" title={ref.referrer}>
                        {ref.referrer?.replace(/^https?:\/\//, '').split('/')[0] || 'Direct'}
                      </span>
                      <span className="text-sm text-gray-500 ml-2">{ref.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <p className="text-gray-400 text-sm">No referrer data yet</p>
                  <p className="text-xs text-gray-300 mt-1">Referrers show where visitors come from</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      {/* Daily View Tab */}
      {activeTab === 'daily' && (
        <DailyAnalyticsView token={token} />
      )}

      {/* Geographic Tab */}
      {activeTab === 'geographic' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Countries */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Globe className="w-4 h-4 text-blue-600" />
              Visitors by Country
            </h3>
            {data?.geographic?.countries?.length > 0 ? (
              <div className="space-y-3">
                {data.geographic.countries.slice(0, 10).map((country, index) => {
                  const maxVisitors = data.geographic.countries[0]?.visitors || 1;
                  const percent = Math.round((country.visitors / maxVisitors) * 100);
                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">{getCountryFlag(country.countryCode)}</span>
                          <span className="text-sm text-gray-700">{country.country}</span>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{country.visitors} visitors</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div 
                          className="bg-blue-500 h-1.5 rounded-full transition-all"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8">
                <Globe className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No geographic data yet</p>
                <p className="text-xs text-gray-300 mt-1">Location tracking requires visitor activity</p>
              </div>
            )}
          </div>

          {/* Cities */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <MapPin className="w-4 h-4 text-purple-600" />
              Top Cities
            </h3>
            {data?.geographic?.cities?.length > 0 ? (
              <div className="space-y-3">
                {data.geographic.cities.slice(0, 10).map((city, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-sm w-5">{index + 1}.</span>
                      <div>
                        <p className="text-sm text-gray-700">{city.city}</p>
                        <p className="text-xs text-gray-400">{city.country}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-medium text-gray-900">{city.visitors}</p>
                      <p className="text-xs text-gray-400">{city.views} views</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <MapPin className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-400 text-sm">No city data yet</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Click Tracking Tab */}
      {activeTab === 'clicks' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Crosshair className="w-4 h-4 text-purple-600" />
              Click Analytics
            </h3>
            <ClickHeatmapVisual clickData={data?.clicks} />
          </div>

          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <Activity className="w-4 h-4 text-green-600" />
              How Click Tracking Works
            </h3>
            <div className="space-y-4 text-sm text-gray-600">
              <p>Click tracking automatically records where visitors interact on your website:</p>
              <ul className="space-y-2 list-disc list-inside">
                <li>Tracks clicks on buttons, links, and interactive elements</li>
                <li>Records element type and text for detailed analysis</li>
                <li>Privacy-friendly - no personal data collected</li>
                <li>Helps identify popular CTAs and navigation patterns</li>
              </ul>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
                <p className="text-blue-800 text-xs">
                  <strong>Note:</strong> Click tracking is automatically enabled on your public website. Data appears here as visitors interact with your content.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Traffic Patterns Tab */}
      {activeTab === 'traffic' && (
        <div className="space-y-6">
          {/* Traffic Time Heatmap */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-medium text-gray-900 flex items-center gap-2">
                <Zap className="w-4 h-4 text-amber-600" />
                Weekly Traffic Heatmap
              </h3>
              {data?.trafficByTime && (
                <div className="flex gap-4 text-sm">
                  <div className="text-gray-600">
                    Peak: <span className="font-medium text-gray-900">{data.trafficByTime.peakHour}:00</span>
                  </div>
                  <div className="text-gray-600">
                    Quiet: <span className="font-medium text-gray-900">{data.trafficByTime.quietHour}:00</span>
                  </div>
                </div>
              )}
            </div>
            <TrafficHeatmap matrixData={data?.trafficByTime?.matrix} />
          </div>

          {/* Daily breakdown chart */}
          <div className="bg-white border border-gray-200 rounded-xl p-5">
            <h3 className="font-medium text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-4 h-4 text-blue-600" />
              Traffic Trend (Last {period} Days)
            </h3>
            <SimpleBarChart data={data?.dailyTraffic} />
            <div className="flex justify-between text-xs text-gray-400 mt-2">
              <span>Older</span>
              <span>Recent</span>
            </div>
          </div>

          {/* Insights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-5">
              <div className="text-blue-600 text-sm font-medium mb-1">Best Time to Post</div>
              <div className="text-2xl font-bold text-blue-900">
                {data?.trafficByTime?.peakHour !== undefined ? `${data.trafficByTime.peakHour}:00` : '--:--'}
              </div>
              <p className="text-xs text-blue-600 mt-1">Highest traffic hour</p>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-5">
              <div className="text-green-600 text-sm font-medium mb-1">Avg Daily Views</div>
              <div className="text-2xl font-bold text-green-900">
                {data?.dailyTraffic?.length > 0 
                  ? Math.round(data.dailyTraffic.reduce((a, b) => a + b.views, 0) / data.dailyTraffic.length)
                  : 0}
              </div>
              <p className="text-xs text-green-600 mt-1">Over selected period</p>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-5">
              <div className="text-purple-600 text-sm font-medium mb-1">Total Sessions</div>
              <div className="text-2xl font-bold text-purple-900">{formatNumber(overview.totalSessions || 0)}</div>
              <p className="text-xs text-purple-600 mt-1">Unique browsing sessions</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 flex items-center gap-2">
          <Activity className="w-4 h-4" />
          About Analytics
        </h4>
        <p className="text-sm text-blue-700 mt-1">
          Analytics data is collected automatically when visitors browse your website. 
          Tracking is privacy-friendly and does not use cookies for identification. 
          Geographic data is derived from IP addresses and may have limited accuracy.
          Data refreshes automatically every minute.
        </p>
      </div>
    </div>
  );
};

export default AnalyticsDashboard;
